import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Button,
  Text,
  QuantaFormik,
  QuantaForm,
  useResponsiveParams,
  InputGrid,
  useQuantaFormikContext,
  Card,
} from 'quanta-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

export default function ModalAddMembers({
  setModalAddMemberDisplay,
  modalAddMemberDisplay,
  memberList,
  genderList,
  setFieldValue,
  insuredDetails,
  countEditsOpen,
  setEnableRenew,
  pedValue
}) {
  const [modalDisplay, setModalDisplay] = useState(false);
  const [isMemberAdding, setIsMemberAdding] = useState(false);
  const addMemberRef = useRef();
  const noOfColsMobile = 1,
    noOfColsDesktop = 2;

  const inputColSpans = useResponsiveParams({
    for_mobile: noOfColsMobile,
    for_desktop: noOfColsDesktop,
  });
  const isFragmentRequired = useResponsiveParams({
    for_mobile: true,
    for_desktop: false,
  });
  const getDomainData = (
    domainArr,
    searchText,
    searchType,
    returnType,
  ) => {
    return domainArr.find(
      (d) =>
        d[`${searchType}`]?.toString()?.toLowerCase() ===
        searchText?.toString()?.toLowerCase(),
    )
      ? domainArr.find(
          (d) =>
            d[`${searchType}`]?.toString()?.toLowerCase() ===
            searchText?.toString()?.toLowerCase(),
        )[`${returnType}`]
      : '';
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setModalDisplay(modalAddMemberDisplay);
    }, 150);
    return () => clearTimeout(timer);
  }, [modalAddMemberDisplay, isMemberAdding, setModalDisplay]);

  const findNextLabel = (label) => {
    let maxxNumber = 0;
    let memArr = Object.keys(insuredDetails)?.filter(
      (insured) => !insuredDetails[insured]?.isHardDeleted,
    );
    for (let i = 0; i < memArr.length; i++) {
      if (
        memArr[i].startsWith(label) &&
        memArr[i].split(label)[1] !== ''
      ) {
        maxxNumber = Math.max(
          maxxNumber,
          parseInt(memArr[i].split(label)[1]),
        );
      }
    }
    return maxxNumber + 1;
  };

  const initialValues = {
    relationship: '',
    gender: '',
    dob: '',
  };

  const validate = (values) => {
    const errors = {};
    if (!values?.relationship) {
      errors.relationship = '* Required';
    }
    if (!values?.gender) {
      errors.gender = '* Required';
    }
    if (!values?.dob) {
      errors.dob = '* Required';
    }
    return errors;
  };
  
  const AddMemberForm = () => {
    const { values, resetForm } = useQuantaFormikContext();
  

    const addMembersData = [
      {
        type: 'select',
        size: 'full',
        label: (
          <Text type="label">
            Relationship <Text severity="error">*</Text>
          </Text>
        ),
        disableSelect: true,
        name: 'relationship',
        colSpan: inputColSpans,
        data:
          memberList && memberList?.length > 0
            ? memberList?.map((mem) => {
                return { label: mem.label, value: mem.label };
              })
            : [],
        onDataChange: (val, setFieldValue) => {
          const gender = getDomainData(
            genderList,
            memberList?.find(
              (m) => m?.label?.toLowerCase() === val?.toLowerCase(),
            )?.gender,
            'value',
            'code',
          )?.toString();
          if (gender) {
            setFieldValue('gender', gender);
          }
        },
      },
      {
        type: 'select',
        disableSelect: true,
        size: 'full',
        disabled:
          getDomainData(
            genderList,
            memberList?.find(
              (mem) =>
                mem?.label?.toLowerCase() ===
                values?.relationship?.toLowerCase(),
            )?.gender,
            'value',
            'code',
          ) !== '',
        label: (
          <Text type="label">
            Gender <Text severity="error">*</Text>
          </Text>
        ),
        name: 'gender',
        data:
          genderList && genderList?.length > 0
            ? genderList?.map((m) => {
                return {
                  label: m?.value ? m?.value : '',
                  value: m?.code ? m?.code : '',
                };
              })
            : [],
      },
      {
        type: 'date',
        label: (
          <Text type="label">
            Date of Birth <Text severity="error">*</Text>
          </Text>
        ),
        name: 'dob',
        calendarPosition: 'top',
      },
    ];

    return (
      <QuantaForm>
        <div className="flex flex-col space-y-2">
          <InputGrid
            data={addMembersData}
            repeat={inputColSpans}
            titleType="title"
            titleSeverity="info"
            size="large"
            gapX={4}
          />
          <div className="flex justify-end w-full mb-2">
            <Button
              size="small"
              type="button"
              className={`w-24`}
              disabled={
                !values?.relationship ||
                !values?.gender ||
                !values?.dob ||
                isMemberAdding
              }
              iconPre={
                isMemberAdding ? (
                  <FontAwesomeIcon icon={faSpinner} spin={true} />
                ) : undefined
              }
              onClick={(_) => {
                setIsMemberAdding(true);
                setEnableRenew(false);
                setFieldValue(`insuredDetails`, {
                  ...insuredDetails,
                  [memberList?.find(
                    (mem) =>
                      mem?.label?.toLowerCase() ===
                      values?.relationship?.toLowerCase(),
                  )?.maxAllowed === 1
                    ? `${values?.relationship}`
                    : `${values?.relationship} ${findNextLabel(
                        values?.relationship,
                      )}`]: {
                    title: '',
                    gender: values?.gender,
                    isGenderReadOnly:
                      getDomainData(
                        genderList,
                        memberList?.find(
                          (mem) =>
                            mem?.label?.toLowerCase() ===
                            values?.relationship?.toLowerCase(),
                        )?.gender,
                        'value',
                        'code',
                      ) !== '',
                    dob: values?.dob,
                    age: `${moment().diff(
                      moment(values?.dob, 'DD/MM/YYYY'),
                      'years',
                    )}`,
                    firstName: '',
                    middleName: '',
                    lastName: '',
                    maritalStatus: '',
                    height: '',
                    weight: '',
                    occupation: '',
                    otherOccupation: '',
                    pedWaitingPeriod: pedValue?.toString(),
                    isMemberDeleted: false,
                    isSoftAdded: true,
                    isHardDeleted: false,
                    memberID: '',
                    memberStatus: '',
                    isDeleted: false,
                    cbBonusPct: null,
                    isPortabilityRequired: false,
                    ppcTestLevel: '',
                  },
                });
                setFieldValue(`countEditsOpen`, countEditsOpen + 1);
                resetForm();
                setModalAddMemberDisplay(false);
                document.body.classList.remove('overflow-y-hidden');
                setIsMemberAdding(false);
              }}
            >
              Add
            </Button>
          </div>
        </div>
      </QuantaForm>
    );
  };

  return (
    <Modal
      display={modalDisplay}
      size={isFragmentRequired ? 'full' : 'small'}
      title="Add Member"
      severity="action"
      onClose={() => {
        addMemberRef?.current?.resetForm();
        setModalAddMemberDisplay(false);
      }}
      scrollRequired={false}
      useFragment={isFragmentRequired}
      fragmentSizeRequired={isFragmentRequired}
      className={
        isFragmentRequired
          ? 'min-h-screen flex flex-col justify-start'
          : ''
      }
    >
      {isFragmentRequired ? (
        <Card
          severity={'action'}
          title={'Add Member'}
          className={'min-h-screen'}
    >
      <QuantaFormik
        innerRef={addMemberRef}
        {...{
          initialValues,
          validate,
        }}
      >
        <AddMemberForm />
      </QuantaFormik>
        </Card>
      ) : (
        <QuantaFormik
          innerRef={addMemberRef}
          {...{
            initialValues,
            validate,
          }}
        >
          <AddMemberForm />
        </QuantaFormik>
      )}
    </Modal>
  );
}
